import Stack from "@mui/material/Stack";
import { useCallback, useMemo } from "react";
import { AxisLabelsFormatterContextObject } from "highcharts";
import { HighchartsProvider } from "@/storybook/c8r-highcharts/common/components/HighchartsProvider";
import { MultiTypeHighcharts } from "@/storybook/c8r-highcharts/MultiTypeHighcharts";
import { budgetDetailsAccumulatedCostChartDataSelector } from "@/store/budgets/selectors/details-drawer/accumulated-cost/budgetDetailsAccumulatedCostChartDataSelector";
import { useAppSelector } from "@/store/hooks";
import { budgetDetailsPeriodSelector } from "@/store/budgets/selectors/details-drawer/budgetDetailsPeriodSelector";
import { getHighChartsYAxisLabelsFormatter } from "@/storybook/c8r-highcharts/common/utils/helpers/getHighChartsYAxisLabelsFormatter";
import { formatDate } from "@/utils/helpers/date-time/datetime-format";
import { budgetDetailsAccumulatedCostBudgetSelector } from "@/store/budgets/selectors/details-drawer/accumulated-cost/budgetDetailsAccumulatedCostBudgetSelector";
import { budgetDetailsAccumulatedCostDataSelector } from "@/store/budgets/selectors/details-drawer/accumulated-cost/budgetDetailsAccumulatedCostDataSelector";
import { getBudgetDetailsAccumulatedCostChartTooltipValueGetter } from "@/components/pages/budgets-and-alerts/components/drawer/budget-details-drawer/utils/helpers/getBudgetDetailsAccumulatedCostChartTooltipValueGetter";
import { budgetDetailsAccumulatedCostLoadingSelector } from "@/store/budgets/selectors/details-drawer/accumulated-cost/budgetDetailsAccumulatedCostLoadingSelector";
import { budgetDetailsByIdLoadingSelector } from "@/store/budgets/selectors/details-drawer/budgetDetailsByIdLoadingSelector";

export const BudgetDetailsAccumulatedCostChart = () => {
  const allCosts = useAppSelector(budgetDetailsAccumulatedCostDataSelector);
  const chartData = useAppSelector(
    budgetDetailsAccumulatedCostChartDataSelector,
  );
  const period = useAppSelector(budgetDetailsPeriodSelector);
  const budget = useAppSelector(budgetDetailsAccumulatedCostBudgetSelector);

  const detailsLoading = useAppSelector(budgetDetailsByIdLoadingSelector);
  const loading = useAppSelector(budgetDetailsAccumulatedCostLoadingSelector);

  const tooltipValueFormatter = useMemo(
    () =>
      getBudgetDetailsAccumulatedCostChartTooltipValueGetter(
        budget,
        allCosts,
        period,
      ),
    [budget, allCosts, period],
  );

  const xAxisLabelsFormatter = useCallback(
    (params: AxisLabelsFormatterContextObject) =>
      formatDate(`${params.value}`, {
        type: period === "annually" ? "monthNameYear" : "dayMonthName",
      }),
    [period],
  );

  return (
    <HighchartsProvider>
      <Stack height={230}>
        <MultiTypeHighcharts
          showLegends
          maxHeight={230}
          chartType="line"
          chartScale="linear"
          chartData={chartData}
          tooltipOptions={tooltipOptions}
          loading={loading || detailsLoading}
          yAxisLabelsFormatter={yAxisLabelsFormatter}
          xAxisLabelsFormatter={xAxisLabelsFormatter}
          tooltipMarkupGenerator={tooltipValueFormatter}
        />
      </Stack>
    </HighchartsProvider>
  );
};

const tooltipOptions = {
  shared: true,
};

const yAxisLabelsFormatter = getHighChartsYAxisLabelsFormatter("cost");
